/**
 * dataworkman Website JS Logic
 * Core Interactions, Animations, Form Validation, and Multi-language Support
 */

import { translations } from './i18n.js';

document.addEventListener('DOMContentLoaded', () => {
  // 1. Language Translation & Switcher System
  let currentLang = localStorage.getItem('dataworkman-lang') || 'ko';

  const updateLanguage = (lang) => {
    currentLang = lang;
    localStorage.setItem('dataworkman-lang', lang);
    document.documentElement.lang = lang;

    // Translate all elements with data-i18n attribute
    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(el => {
      const key = el.getAttribute('data-i18n');
      if (translations[lang] && translations[lang][key]) {
        const text = translations[lang][key];
        
        // Handle input placeholders and textareas
        if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
          el.placeholder = text;
        } else {
          // Handle standard DOM elements (supports HTML tags like br, span)
          el.innerHTML = text;
        }
      }
    });

    // Translate select box dynamic placeholders
    const projectType = document.getElementById('project-type');
    if (projectType && projectType.options[0]) {
      projectType.options[0].text = translations[lang]["contact.form.type.placeholder"];
    }

    // Form inputs placeholders (Fallback update)
    const nameInput = document.getElementById('client-name');
    if (nameInput) nameInput.placeholder = lang === 'ko' ? '홍길동' : 'John Doe';
    
    const companyInput = document.getElementById('client-company');
    if (companyInput) companyInput.placeholder = lang === 'ko' ? 'dataworkman 주식회사' : 'dataworkman Inc.';
    
    const descInput = document.getElementById('project-desc');
    if (descInput) {
      descInput.placeholder = translations[lang]["contact.form.desc.placeholder"];
    }

    // Sync active switcher button state (both desktop and mobile)
    const langButtons = document.querySelectorAll('.lang-btn');
    langButtons.forEach(btn => {
      if (btn.getAttribute('data-lang') === lang) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });

    // Re-trigger Lucide icon rendering just in case
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  };

  // Bind Language Button Clicks
  const langButtons = document.querySelectorAll('.lang-btn');
  langButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const lang = btn.getAttribute('data-lang');
      updateLanguage(lang);
    });
  });

  // Apply initial language on load
  updateLanguage(currentLang);

  // 2. Initialize Lucide Icons
  if (typeof lucide !== 'undefined') {
    lucide.createIcons();
  }

  // 3. Sticky Header Scroll Effect
  const header = document.getElementById('header');
  const handleHeaderScroll = () => {
    if (window.scrollY > 50) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  };
  window.addEventListener('scroll', handleHeaderScroll);
  handleHeaderScroll(); // 초기 실행

  // 4. Mobile Navigation Menu Toggle
  const mobileToggle = document.getElementById('mobile-toggle');
  const mobileNav = document.getElementById('mobile-nav');
  const menuIcon = document.getElementById('menu-icon');
  
  if (mobileToggle && mobileNav) {
    mobileToggle.addEventListener('click', () => {
      const isActive = mobileNav.classList.toggle('active');
      
      // Toggle Menu Icon (Menu <=> X)
      if (isActive) {
        menuIcon.setAttribute('data-lucide', 'x');
      } else {
        menuIcon.setAttribute('data-lucide', 'menu');
      }
      
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    });

    // Close mobile nav when link is clicked (except language buttons)
    const mobileLinks = mobileNav.querySelectorAll('.mobile-link, .mobile-btn');
    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        mobileNav.classList.remove('active');
        menuIcon.setAttribute('data-lucide', 'menu');
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      });
    });
  }

  // 5. Scroll Reveal Animation (Intersection Observer)
  const animatableElements = document.querySelectorAll('.animate-on-scroll');
  const revealCallback = (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('appear');
        observer.unobserve(entry.target);
      }
    });
  };

  const revealObserver = new IntersectionObserver(revealCallback, {
    root: null,
    threshold: 0.15,
    rootMargin: '0px 0px -50px 0px'
  });

  animatableElements.forEach(el => revealObserver.observe(el));

  // 6. Scroll Spy (Active Menu Navigation Highlight)
  const sections = document.querySelectorAll('section');
  const navLinks = document.querySelectorAll('.nav-link');

  const scrollSpy = () => {
    let currentId = '';
    const scrollPosition = window.scrollY + 160; // 헤더 높이만큼 오프셋 설정

    sections.forEach(section => {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
        currentId = section.getAttribute('id');
      }
    });

    navLinks.forEach(link => {
      link.classList.remove('active');
      if (link.getAttribute('href') === `#${currentId}`) {
        link.classList.add('active');
      }
    });
  };

  window.addEventListener('scroll', scrollSpy);
  scrollSpy(); // 초기 실행

  // 7. Contact Form Validation & Submission
  const contactForm = document.getElementById('contact-form');
  const successModal = document.getElementById('success-modal');
  const modalCloseBtn = document.getElementById('modal-close-btn');
  const formStatus = document.getElementById('form-status');

  if (contactForm) {
    const inputs = contactForm.querySelectorAll('input[required], select[required], textarea[required]');

    // 실시간 입력 감지하여 에러 제거
    inputs.forEach(input => {
      input.addEventListener('input', () => {
        if (input.value.trim() !== '') {
          input.closest('.form-group').classList.remove('has-error');
        }
      });
      if (input.tagName === 'SELECT') {
        input.addEventListener('change', () => {
          if (input.value !== '') {
            input.closest('.form-group').classList.remove('has-error');
          }
        });
      }
    });

    contactForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      let hasError = false;
      if (formStatus) {
        formStatus.textContent = '';
        formStatus.classList.remove('is-error');
      }

      // 필수 입력 필드 검증
      inputs.forEach(input => {
        const formGroup = input.closest('.form-group');
        
        // 이메일 유효성 특수 검사
        if (input.type === 'email') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(input.value.trim())) {
            formGroup.classList.add('has-error');
            hasError = true;
          } else {
            formGroup.classList.remove('has-error');
          }
        } else {
          // 일반 필수 값 검사
          if (input.value.trim() === '') {
            formGroup.classList.add('has-error');
            hasError = true;
          } else {
            formGroup.classList.remove('has-error');
          }
        }
      });

      if (!hasError) {
        const submitBtn = document.getElementById('submit-btn');
        const submitBtnSpan = submitBtn.querySelector('span');
        const formData = new FormData(contactForm);
        const payload = Object.fromEntries(formData.entries());
        
        submitBtn.disabled = true;
        submitBtnSpan.innerHTML = translations[currentLang]["val.sending"];
        submitBtn.innerHTML = `<i data-lucide="loader-2" class="spin"></i> <span>${translations[currentLang]["val.sending"]}</span>`;
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }

        try {
          const response = await fetch('/api/contact', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            throw new Error(`Request failed: ${response.status}`);
          }

          if (successModal) {
            successModal.classList.add('active');
          }

          contactForm.reset();
        } catch (error) {
          if (formStatus) {
            formStatus.textContent = translations[currentLang]["val.error.submit"];
            formStatus.classList.add('is-error');
          }
        } finally {
          submitBtn.disabled = false;
          submitBtn.innerHTML = `<span>${translations[currentLang]["contact.form.submit"]}</span> <i data-lucide="send"></i>`;
          if (typeof lucide !== 'undefined') {
            lucide.createIcons();
          }
        }
      }
    });
  }

  // 모달 닫기
  if (modalCloseBtn && successModal) {
    modalCloseBtn.addEventListener('click', () => {
      successModal.classList.remove('active');
    });
  }
});
